import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Rocket, PartyPopper, Linkedin, Share2, Loader2, Twitter } from "lucide-react";
import { EMAIL } from "@/lib/email-config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { reportError } from "@/lib/error-reporting";
import { toast } from "@/hooks/use-toast";
import { WAITLIST_POSITION_OFFSET } from "@/lib/waitlist-config";

const ROLE_OPTIONS = [
  "Individual Contributor",
  "Team Lead",
  "Manager",
  "Director",
  "VP/Executive",
  "Founder",
  "Career Changer",
  "Student",
  "Other",
];

const REFERRAL_OPTIONS = [
  "LinkedIn",
  "Twitter/X",
  "Friend/Colleague",
  "Podcast",
  "Search Engine",
  "Newsletter",
  "Other",
];

const waitlistSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address").max(255),
  first_name: z.string().trim().max(100).optional(),
  role: z.string().optional(),
  referral_source: z.string().optional(),
});

type WaitlistFormData = z.infer<typeof waitlistSchema>;

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [position, setPosition] = useState<number | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [hasShared, setHasShared] = useState(false);

  const form = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: {
      email: "",
      first_name: "",
      role: "",
      referral_source: "",
    },
  });

  const sendConfirmationEmail = async (email: string, firstName: string, displayPosition: number) => {
    try {
      await supabase.functions.invoke('send-waitlist-confirmation', {
        body: { email, firstName, position: displayPosition },
      });
      console.log('Waitlist confirmation email sent');
    } catch (error) {
      reportError(error, 'waitlist', { action: 'send-confirmation-email' });
      // Don't fail the whole submission if email fails
    }
  };

  const onSubmit = async (data: WaitlistFormData) => {
    setIsSubmitting(true);

    try {
      // Capture UTM parameters
      const utm_source = searchParams.get("utm_source") || undefined;
      const utm_medium = searchParams.get("utm_medium") || undefined;
      const utm_campaign = searchParams.get("utm_campaign") || undefined;

      // Check if email already exists using secure RPC
      const { data: checkData, error: checkError } = await supabase
        .rpc('check_waitlist_email', { p_email: data.email.toLowerCase() });

      if (checkError) {
        reportError(checkError, 'waitlist', { action: 'check-email' });
        throw checkError;
      }

      // Type assertion for RPC response
      const checkResult = checkData as { exists: boolean; position: number | null } | null;

      if (checkResult?.exists) {
        // User is already on the list - show their position
        const displayPosition = (checkResult.position || 0) + WAITLIST_POSITION_OFFSET;
        setPosition(displayPosition);
        setSubmittedEmail(data.email);
        setIsReturningUser(true);
        setSuccess(true);
        toast({
          title: "You're already on the list!",
          description: `We'll notify you at ${data.email} when it's your turn.`,
        });
        return;
      }

      // Insert new waitlist entry
      // IMPORTANT: do not request returned row data here. With RLS enabled and SELECT locked down,
      // PostgREST can reject the INSERT when "return=representation" is requested.
      const { error } = await supabase
        .from("waitlist")
        .insert({
          email: data.email.toLowerCase(),
          first_name: data.first_name || null,
          role: data.role || null,
          referral_source: data.referral_source || null,
          utm_source,
          utm_medium,
          utm_campaign,
          landing_page: window.location.pathname,
        });

      if (error) throw error;

      // Calculate position using secure RPC
      const { data: positionData } = await supabase
        .rpc('check_waitlist_email', { p_email: data.email.toLowerCase() });

      // Type assertion for RPC response
      const positionResult = positionData as { exists: boolean; position: number | null } | null;
      const displayPosition = (positionResult?.position || 0) + WAITLIST_POSITION_OFFSET;
      setPosition(displayPosition);
      setSubmittedEmail(data.email);
      setSuccess(true);

      // Send confirmation email (fire and forget)
      sendConfirmationEmail(data.email.toLowerCase(), data.first_name || 'there', displayPosition);

      toast({
        title: "You're on the list!",
        description: "Check your email for confirmation.",
      });
    } catch (error: any) {
      reportError(error, 'waitlist', { action: 'submit' });
      toast({
        title: "Something went wrong",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setSuccess(false);
      setIsReturningUser(false);
      setPosition(null);
      setSubmittedEmail("");
      setHasShared(false);
      form.reset();
    }, 200);
  };

  const handleInviteCodeClick = () => {
    handleClose();
    navigate("/auth?mode=invite");
  };

  const shareUrl = "https://momentumeq.app";
  const shareText = "I just joined the MomentumEQ waitlist! A personalized career development platform that helps you build momentum through daily habits. Check it out:";

  const [sharedPlatforms, setSharedPlatforms] = useState<string[]>([]);

  // Generate signed token for secure boost requests
  const generateBoostToken = async (email: string, platform: string): Promise<string | null> => {
    try {
      // Create payload
      const payload = {
        email: email.toLowerCase(),
        platform,
        timestamp: Date.now(),
      };
      
      // Encode payload to base64
      const payloadB64 = btoa(JSON.stringify(payload));
      
      // We need the server to generate the signature since MAGIC_LINK_SECRET is server-side
      // Use a simple approach: generate a request-specific token using the session info
      const { data, error } = await supabase.functions.invoke('generate-boost-token', {
        body: { email: email.toLowerCase(), platform },
      });
      
      if (error || !data?.token) {
        reportError(error, 'waitlist', { action: 'generate-boost-token' });
        return null;
      }

      return data.token;
    } catch (error) {
      reportError(error, 'waitlist', { action: 'generate-boost-token' });
      return null;
    }
  };

  const boostPosition = async (platform: string, shareUrl: string) => {
    // Check if already shared on this platform (local check)
    if (sharedPlatforms.includes(platform)) {
      toast({
        title: "Already shared!",
        description: `You've already boosted your position via ${platform === "linkedin" ? "LinkedIn" : "Twitter/X"}.`,
      });
      // Still open the share window
      window.open(shareUrl, "_blank", platform === "linkedin" ? "width=600,height=600" : "width=600,height=400");
      return;
    }

    // Generate secure token BEFORE opening share window
    const token = await generateBoostToken(submittedEmail, platform);
    
    if (!token) {
      toast({
        title: "Unable to verify",
        description: "Could not generate verification token. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Open share window
    window.open(shareUrl, "_blank", platform === "linkedin" ? "width=600,height=600" : "width=600,height=400");

    try {
      const { data, error } = await supabase.functions.invoke('boost-waitlist-position', {
        body: { token, platform },
      });

      if (error) throw error;

      if (data.success) {
        setPosition(data.newPosition);
        setSharedPlatforms(data.socialShares || [...sharedPlatforms, platform]);
        setHasShared(true);
        toast({
          title: "🚀 Position boosted!",
          description: `You moved up ${data.boost} spots! Thanks for sharing.`,
        });
      } else if (data.error === "already_shared") {
        setSharedPlatforms(prev => prev.includes(platform) ? prev : [...prev, platform]);
        toast({
          title: "Already shared!",
          description: data.message,
        });
      }
    } catch (error) {
      reportError(error, 'waitlist', { action: 'boost-position' });
      toast({
        title: "Boost failed",
        description: "Unable to update your position. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    boostPosition("linkedin", linkedInUrl);
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    boostPosition("twitter", twitterUrl);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {!success ? (
          <>
            <DialogHeader className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-primary" />
              </div>
              <DialogTitle className="text-2xl font-bold">
                Get Early Access
              </DialogTitle>
              <p className="text-muted-foreground text-sm">
                Join the waitlist to be first in line when we open to new users.
                Early members get exclusive benefits.
              </p>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="you@company.com"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Your name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>What's your current role? (Optional)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select role..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background border">
                          {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="referral_source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How did you hear about us? (Optional)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select source..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background border">
                          {REFERRAL_OPTIONS.map((source) => (
                            <SelectItem key={source} value={source}>
                              {source}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    "Join Waitlist"
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We'll never spam you. Unsubscribe anytime.
                </p>
              </form>
            </Form>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-center text-muted-foreground">
                Already have an invite code?{" "}
                <button
                  onClick={handleInviteCodeClick}
                  className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
                >
                  Enter it here
                </button>
              </p>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <PartyPopper className="w-6 h-6 text-accent" />
              </div>
              <DialogTitle className="text-2xl font-bold">
                {isReturningUser ? "Welcome back!" : "You're on the list!"}
              </DialogTitle>
              {isReturningUser ? (
                <p className="text-muted-foreground text-sm">
                  You're already on the waitlist at <span className="font-medium text-foreground break-all">{submittedEmail}</span>. We'll notify you when it's your turn!
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground text-sm">
                    We've sent a confirmation to <span className="font-medium text-foreground break-all">{submittedEmail}</span>.
                  </p>
                  <p className="text-xs text-muted-foreground/80 mt-1">
                    📬 Check your spam/junk folder and add <span className="font-medium">{EMAIL.support}</span> to your contacts.
                  </p>
                </>
              )}
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {position && (
                <div className="text-center py-4 px-4 rounded-lg bg-muted/50 border">
                  <p className="text-sm text-muted-foreground">Your position</p>
                  <p className="text-3xl font-bold text-primary">#{position}</p>
                </div>
              )}

              {/* Gamification CTA */}
              <div className="text-center py-4 px-4 rounded-lg bg-gradient-to-br from-accent/10 to-primary/10 border border-accent/30">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-lg">🚀</span>
                  <span className="text-sm font-semibold text-accent">Move up the list!</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Share and jump ahead in the queue
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={handleLinkedInShare}
                    disabled={sharedPlatforms.includes("linkedin")}
                    className={sharedPlatforms.includes("linkedin")
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "bg-[#0A66C2] hover:bg-[#084e96] text-white"
                    }
                  >
                    <Linkedin className="mr-1.5 h-4 w-4 shrink-0" />
                    <span className="truncate">{sharedPlatforms.includes("linkedin") ? "Shared" : "LinkedIn +50"}</span>
                  </Button>
                  <Button
                    onClick={handleTwitterShare}
                    disabled={sharedPlatforms.includes("twitter")}
                    className={sharedPlatforms.includes("twitter")
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "bg-black hover:bg-neutral-800 text-white"
                    }
                  >
                    <Twitter className="mr-1.5 h-4 w-4 shrink-0" />
                    <span className="truncate">{sharedPlatforms.includes("twitter") ? "Shared" : "X/Twitter +25"}</span>
                  </Button>
                </div>
                {sharedPlatforms.length > 0 && (
                  <p className="text-xs text-accent mt-2">
                    ✓ Thanks for sharing! {sharedPlatforms.length === 2 ? "You earned +75 spots total!" : `You earned +${sharedPlatforms.includes("linkedin") ? 50 : 25} spots!`}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open("https://linkedin.com/company/momentumeq/", "_blank")}
                >
                  <Linkedin className="mr-1.5 h-4 w-4 shrink-0" />
                  <span className="truncate">Follow</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open("https://x.com/MomentumEQ", "_blank")}
                >
                  <Twitter className="mr-1.5 h-4 w-4 shrink-0" />
                  <span className="truncate">Follow</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: "MomentumEQ", text: shareText, url: shareUrl });
                    } else {
                      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                      toast({ title: "Link copied!", description: "Share it with your network." });
                    }
                  }}
                >
                  <Share2 className="mr-1.5 h-4 w-4 shrink-0" />
                  <span className="truncate">Copy</span>
                </Button>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-center text-muted-foreground">
                Received an invite code?{" "}
                <button
                  onClick={handleInviteCodeClick}
                  className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
                >
                  Enter it here
                </button>
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
